/**
 * MaterialDrawer - Material Design 3 Navigation Drawer
 * Mobile-first, app-like design optimized for low-tech users
 * 
 * Features:
 * - Slides in from left
 * - Backdrop overlay (scrim)
 * - Header with user info and photo
 * - Navigation items (Profile, Settings, About)
 * - Logout at bottom
 * - Smooth slide animation (300ms)
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  X, User, LogOut, Info
} from 'lucide-react';
import { useAuthContext } from '../../../contexts/AuthContext';
import { getPhotoLocal, getPhotoFromBackend } from '../../../utils/photoStorage';
import { getIconSize } from '../../../components/common/mobile/IconSizes';

/**
 * MaterialDrawer Component
 * @param {Object} props
 * @param {boolean} props.open - Whether drawer is open
 * @param {Function} props.onClose - Callback when drawer should close
 * @param {Function} props.onLogout - Callback when logout is clicked
 */
export default function MaterialDrawer({ open = false, onClose, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getToken } = useAuthContext();
  const drawerRef = useRef(null);
  const backdropRef = useRef(null);
  const [userPhoto, setUserPhoto] = useState(null);

  // Close drawer when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (open && backdropRef.current && backdropRef.current === event.target) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (open && event.key === 'Escape') {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  // Get user display name
  const getUserDisplayName = () => {
    if (!user) return 'Teacher';
    const name = user.name || user.teacher?.name || user.officer?.name || user.principal?.name;
    if (name) return name;
    return `${user.user_type || 'User'}`;
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return 'T';
    const name = user.name || user.teacher?.name || user.officer?.name || user.principal?.name;
    if (!name) return 'T';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  // Get user type label
  const getUserTypeLabel = () => {
    if (!user) return '';
    const typeMap = {
      teacher: 'Teacher',
      officer: 'Cluster Officer',
      principal: 'Principal',
      coordinator: 'JEF Coordinator',
    };
    return typeMap[user.user_type] || user.user_type || '';
  };

  // Load user photo
  useEffect(() => {
    if (!user?.id) return;

    const loadPhoto = async () => {
      try {
        // Try backend first
        if (getToken) {
          const token = getToken();
          if (token) {
            const backendPhoto = await getPhotoFromBackend(user.id, token);
            if (backendPhoto) {
              setUserPhoto(backendPhoto);
              return;
            }
          }
        }

        // Fallback to local storage
        const localPhoto = await getPhotoLocal(user.id);
        if (localPhoto) {
          setUserPhoto(localPhoto);
        }
      } catch (error) {
        console.error('Failed to load user photo:', error);
      }
    };

    loadPhoto();

    // Listen for photo updates
    const handlePhotoUpdate = () => {
      loadPhoto();
    };
    window.addEventListener('photoUpdated', handlePhotoUpdate);

    return () => {
      window.removeEventListener('photoUpdated', handlePhotoUpdate);
    };
  }, [user?.id, getToken]);

  // Handle logout
  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      navigate('/login');
    }
    onClose();
  };

  // Navigation items - simplified to Profile and About (Settings temporarily hidden)
  const navigationItems = [
    { id: 'profile', label: 'Profile', icon: User, path: '/teacher/profile', divider: false },
    { id: 'about', label: 'About', icon: Info, path: '/teacher/about', divider: true },
  ];

  if (!open) return null;

  return (
    <>
      {/* Backdrop (Scrim) */}
      <div
        ref={backdropRef}
        className="fixed inset-0 bg-black/50 z-[1300] transition-opacity duration-300"
        style={{
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
        }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        ref={drawerRef}
        className={`
          fixed top-0 left-0 bottom-0 z-[1300]
          bg-white
          w-full max-w-[320px]
          md:w-[280px]
          shadow-2xl
          transform transition-transform duration-300 ease-out
          flex flex-col
          ${open ? 'translate-x-0' : '-translate-x-full'}
        `}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Header with User Info - Material Expressive */}
        <div className="p-lg border-b border-gray-200/60">
          {/* Close Button */}
          <div className="flex items-center justify-end mb-md">
            <button
              onClick={onClose}
              className="flex items-center justify-center min-h-touch-min min-w-touch-min rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors"
              aria-label="Close menu"
            >
              <X size={getIconSize('large')} className="text-text-secondary" />
            </button>
          </div>

          {/* User Avatar and Info */}
          <div className="flex items-center gap-md mb-md">
            {userPhoto ? (
              <img
                src={userPhoto}
                alt={getUserDisplayName()}
                className="h-12 w-12 rounded-full object-cover flex-shrink-0 border-2 border-primary-500/20"
                onError={() => setUserPhoto(null)} // Fallback to initials if image fails to load
              />
            ) : (
              <div className="h-12 w-12 rounded-full bg-primary-500 text-white flex items-center justify-center flex-shrink-0">
                <span className="text-mobile-body font-semibold">{getUserInitials()}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-mobile-body font-semibold text-text-primary truncate">
                {getUserDisplayName()}
              </p>
              <p className="text-mobile-body-sm text-text-secondary truncate">
                {getUserTypeLabel()}
              </p>
            </div>
          </div>

          {/* Email/Phone (if available) */}
          {(user?.email || user?.phone) && (
            <div className="text-mobile-body-sm text-text-muted">
              {user.email && <p className="truncate">{user.email}</p>}
              {user.phone && <p className="truncate">{user.phone}</p>}
            </div>
          )}
        </div>

        {/* Navigation Items - Material Expressive */}
        <nav className="flex-1 overflow-y-auto py-md">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.path && location.pathname === item.path;

            return (
              <div key={item.id}>
                {item.divider && <div className="h-px bg-gradient-to-r from-transparent via-black/10 to-transparent my-md mx-lg" />}
                <button
                  onClick={() => {
                    if (item.path) {
                      navigate(item.path);
                      onClose();
                    }
                  }}
                  className={`
                    w-full px-lg py-md
                    flex items-center gap-md
                    text-left
                    transition-colors duration-200
                    min-h-touch-min
                    ${isActive
                      ? 'bg-primary-100 text-primary-500 border-l-4 border-primary-500'
                      : 'text-text-secondary hover:bg-gray-50 hover:text-text-primary'
                    }
                  `}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <Icon size={getIconSize('default')} className={`flex-shrink-0 ${
                    isActive ? 'text-primary-500' : 'text-text-secondary'
                  }`} strokeWidth={isActive ? 2.5 : 2} />
                  <span className={`text-mobile-body font-medium ${
                    isActive ? 'text-primary-500' : 'text-text-primary'
                  }`}>
                    {item.label}
                  </span>
                </button>
              </div>
            );
          })}
        </nav>

        {/* Footer: Logout - Material Expressive */}
        <div className="border-t border-gray-200/60 p-md">
          <button
            onClick={handleLogout}
            className="
              w-full px-lg py-md
              flex items-center gap-md
              text-left
              text-alert-error
              hover:bg-red-50
              active:bg-red-100
              transition-colors duration-200
              rounded-xl
              min-h-touch-min
            "
            aria-label="Logout"
          >
            <LogOut size={getIconSize('default')} className="flex-shrink-0" strokeWidth={2} />
            <span className="text-mobile-body font-medium">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}

